import { useEffect, useRef, useState } from "react";
import { Calendar, Clock, Phone, Mail, X as XIcon, CheckCircle2, AlertCircle, ExternalLink, CalendarClock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useCalendar } from "@/hooks/useCalendar";
import { RescheduleEventDialog, DeleteEventDialog } from "@/components/CalendarEventDialogs";

type AppointmentRow = {
  id: string;
  family_name: string;
  family_phone: string | null;
  family_email: string | null;
  scheduled_at: string;
  duration_minutes: number;
  timezone: string;
  status: string;
  meeting_type: string;
  source: string;
  notes: string | null;
  lead_id: string | null;
  google_event_id: string | null;
  google_event_link: string | null;
  calendar_synced_at: string | null;
  completed_at?: string | null;
  canceled_at?: string | null;
};

const STATUS_TONE: Record<string, string> = {
  scheduled: "bg-primary/10 text-primary border-primary/20",
  confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  completed: "bg-muted text-muted-foreground border-border",
  no_show: "bg-amber-100 text-amber-700 border-amber-200",
  canceled: "bg-rose-100 text-rose-700 border-rose-200",
};

function formatLocal(iso: string, tz: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz, weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  }).format(new Date(iso));
}

export default function Appointments() {
  const [items, setItems] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"upcoming" | "past" | "all">("upcoming");
  const [rescheduleTarget, setRescheduleTarget] = useState<AppointmentRow | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AppointmentRow | null>(null);
  const { toast } = useToast();
  const { updateEvent, deleteEvent, creatingEvent } = useCalendar();
  // Only the very first load should show the full-page spinner — refreshing
  // after e.g. a reschedule/cancel shouldn't blank the whole list out from
  // under the user while it silently reloads in the background.
  const hasLoadedOnce = useRef(false);

  async function load() {
    if (!hasLoadedOnce.current) setLoading(true);
    const { data, error } = await supabase
      .from("appointments")
      .select("*")
      .order("scheduled_at", { ascending: true });
    if (error) {
      toast({ title: "Couldn't load appointments", description: error.message, variant: "destructive" });
    } else {
      setItems((data ?? []) as AppointmentRow[]);
    }
    setLoading(false);
    hasLoadedOnce.current = true;
  }

  useEffect(() => { load(); }, []);

  async function updateStatus(id: string, status: string, extra: Partial<AppointmentRow> = {}) {
    const { error } = await supabase
      .from("appointments")
      .update({ status, ...extra })
      .eq("id", id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `Marked as ${status.replace("_", " ")}` });
      await load();
    }
  }

  async function handleConfirmCancel() {
    const a = cancelTarget;
    if (!a) return;
    const { error } = await supabase
      .from("appointments")
      .update({ status: "canceled", canceled_at: new Date().toISOString() })
      .eq("id", a.id);
    if (error) {
      toast({ title: "Cancel failed", description: error.message, variant: "destructive" });
      return;
    }
    if (a.google_event_id) {
      await deleteEvent(a.google_event_id, { appointmentId: a.id });
    }
    toast({ title: "Appointment canceled" });
    setCancelTarget(null);
    await load();
  }

  async function handleConfirmReschedule(startTimeIso: string) {
    const a = rescheduleTarget;
    if (!a) return;
    const { error } = await supabase
      .from("appointments")
      .update({ scheduled_at: startTimeIso })
      .eq("id", a.id);
    if (error) {
      toast({ title: "Reschedule failed", description: error.message, variant: "destructive" });
      return;
    }
    if (a.google_event_id) {
      await updateEvent(a.google_event_id, a.family_name, a.notes || "", startTimeIso, a.duration_minutes, {
        appointmentId: a.id,
        attendeeEmail: a.family_email ?? undefined,
        attendeePhone: a.family_phone ?? undefined,
        location: "Phone Call",
      });
    }
    toast({ title: "Appointment rescheduled" });
    setRescheduleTarget(null);
    await load();
  }

  const now = Date.now();
  const filtered = items.filter((a) => {
    const t = new Date(a.scheduled_at).getTime();
    if (filter === "upcoming") return t >= now - 60 * 60_000 && a.status !== "canceled";
    if (filter === "past") return t < now - 60 * 60_000 || a.status === "completed" || a.status === "no_show";
    return true;
  });

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Calendar className="h-6 w-6 text-primary" /> Appointments
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Phone consultations booked by Sarah and your team.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {(["upcoming", "past", "all"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <Calendar className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-semibold text-foreground">No {filter} appointments</p>
          <p className="mt-1 text-xs text-muted-foreground">
            When Sarah books a call with a family, it'll show up here.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => (
            <li key={a.id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground">{a.family_name}</p>
                    <Badge variant="outline" className={STATUS_TONE[a.status] ?? ""}>
                      {a.status.replace("_", " ")}
                    </Badge>
                  </div>
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    {formatLocal(a.scheduled_at, a.timezone)} · {a.duration_minutes} min
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {a.family_phone && (
                      <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.family_phone}</span>
                    )}
                    {a.family_email && (
                      <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {a.family_email}</span>
                    )}
                  </div>
                  {a.notes && (
                    <p className="mt-2 rounded-md bg-muted/50 p-2 text-xs text-foreground/80">{a.notes}</p>
                  )}
                  {a.google_event_id && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      {a.google_event_link ? (
                        <a
                          href={a.google_event_link}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-primary underline"
                        >
                          Synced to Google Calendar <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span>Synced to Google Calendar</span>
                      )}
                      {a.calendar_synced_at && (
                        <span>· {new Date(a.calendar_synced_at).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                </div>
                {a.status === "scheduled" && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(a.id, "completed", { completed_at: new Date().toISOString() })}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Completed
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(a.id, "no_show")}
                    >
                      <AlertCircle className="h-4 w-4 mr-1" /> No-show
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRescheduleTarget(a)}
                    >
                      <CalendarClock className="h-4 w-4 mr-1" /> Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setCancelTarget(a)}
                    >
                      <XIcon className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <RescheduleEventDialog
        open={!!rescheduleTarget}
        onClose={() => setRescheduleTarget(null)}
        onConfirm={handleConfirmReschedule}
        loading={creatingEvent}
        title="Reschedule appointment"
        description={
          rescheduleTarget?.google_event_id
            ? "Pick a new date and time. The linked Google Calendar event will be updated to match."
            : "Pick a new date and time for this appointment."
        }
        initialStart={rescheduleTarget ? new Date(rescheduleTarget.scheduled_at) : undefined}
      />
      <DeleteEventDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
        loading={creatingEvent}
        title="Cancel this appointment?"
        description={
          cancelTarget?.google_event_id
            ? "This will mark the appointment as canceled and remove the linked event from Google Calendar. This action cannot be undone."
            : "This will mark the appointment as canceled. This action cannot be undone."
        }
        confirmLabel="Cancel appointment"
      />
    </div>
  );
}